import {
    OrganizationUpdateRequest,
    useCompanyCardByIdGetQuery,
    useCompanyCardDeleteMutation,
    useOrganizationDKVCustomerNumberListQuery,
    useOrganizationUpdateMutation,
} from "../../../app/api/api";
import {useTranslation} from "react-i18next";
import {
    DkvCustomerNumber,
    DkvCustomerNumberField,
    Organization,
    OrganizationField,
} from "../../../app/api/model/organization";
import React, {useEffect, useState} from "react";
import {getOrganizationUpdateValidationSchema} from "./validation";
import UpdateDialog from "../../../components/Dialog/UpdateDialog";
import {InputFieldType} from "../../../components/input/factory/types";
import LabeledSection from "../../../components/Layout/Page/LabeledSection";
import {useModal} from "../../../hooks/useModal";
import EditIcon from "@mui/icons-material/Edit";
import {LabeledValue, TitleBarButton} from "../../../components/Layout/Page";
import {getTranslation, TranslationContext} from "../../../utils/getTranslationKey";
import {FormControl, Grid, Typography} from "@mui/material";
import {AddEditCompanyCard} from "../AddEditCompanyCard";
import {
    CompanyCardDetail,
    CompanyCardField,
    CompanyCardServerField,
} from "../../../app/api/model/organization/companyCard";
import {DateFormat, formatTimestamp} from "../../../utils/datetime";
import {EMPTY_UI} from "../../../variables";
import ConfirmDialog from "../../../components/Dialog/ConfirmDialog";
import {useSnackbar} from "notistack";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import {t} from "i18next";
import {AsyncIdListAutocomplete} from "../../../components/input/AsyncAutocomplete/variants";
import {PermissionLevel} from "../../../app/api/model/user";

export interface OrganizationGeneralContentProps {
    organization: Organization;
    readOnlyProperties?: string[];
}

function renderCompanyCard(companyCardDetail: CompanyCardDetail) {
    return (
        <>
            <LabeledValue
                label={t("companyCard_cardId")}
                content={companyCardDetail?.[CompanyCardField.CARD_ID]}
                xs={6}
                md={6}
            />
            <LabeledValue
                label={t("companyCard_cardReaderDrawer")}
                content={companyCardDetail?.[CompanyCardField.CARD_READER_DRAWER]}
                xs={6}
                md={6}
            />
            <LabeledValue
                label={t("companyCard_cardReaderSlot")}
                content={companyCardDetail?.[CompanyCardField.CARD_READER_SLOT]}
                xs={6}
                md={6}
            />
            <LabeledValue
                label={t("companyCard_cardServer")}
                content={companyCardDetail?.[CompanyCardField.CARD_SERVER][CompanyCardServerField.NAME]}
                xs={6}
                md={6}
            />
            <LabeledValue
                label={t("companyCard_validUntil")}
                content={formatTimestamp(companyCardDetail?.[CompanyCardField.VALID_UNTIL], DateFormat.DATE)}
                xs={6}
                md={6}
            />
            <LabeledValue
                label={t("generic_comment")}
                content={companyCardDetail?.[CompanyCardField.COMMENT]}
                xs={12}
                md={12}
            />
        </>
    );
}

export default function OrganizationGeneralContent({
    organization,
    readOnlyProperties = [],
}: OrganizationGeneralContentProps) {
    const {t} = useTranslation();
    const {enqueueSnackbar} = useSnackbar();
    const addEditCompanyCardModal = useModal(false);
    const deleteCompanyCardModal = useModal(false);
    const updateOrganizationModal = useModal(false);
    const [companyCardActionButtons, setCompanyCardActionButtons] = useState<TitleBarButton[] | undefined>(undefined);
    const [updateOrganization] = useOrganizationUpdateMutation();
    const [deleteCompanyCard, {isSuccess: companyCardDeleted}] = useCompanyCardDeleteMutation();
    const {data: companyCardDetail} = useCompanyCardByIdGetQuery(
        {
            organizationId: organization.id,
            id: organization?.companyCard?.[CompanyCardField.ID],
        },
        {skip: !organization.id || !organization?.companyCard}
    );
    const hasCompanyCard = organization?.companyCard;

    useEffect(() => {
        const buttons = [
            {
                key: "addEdit",
                tooltip: hasCompanyCard ? "action_update" : "action_create",
                disabled: addEditCompanyCardModal.isOpen,
                minRequiredPermissionToSee: PermissionLevel.MANAGER,
                callback: () => addEditCompanyCardModal.setIsOpen(true),
                icon: hasCompanyCard ? <EditIcon /> : <AddCircleIcon />,
            },
        ];
        if (hasCompanyCard) {
            buttons.push({
                key: "delete",
                tooltip: "action_delete",
                disabled: deleteCompanyCardModal.isOpen || !hasCompanyCard,
                minRequiredPermissionToSee: PermissionLevel.MANAGER,
                callback: () => deleteCompanyCardModal.setIsOpen(true),
                icon: <DeleteIcon />,
            });
        }
        setCompanyCardActionButtons(buttons);
    }, [JSON.stringify(companyCardDetail), JSON.stringify(organization?.companyCard), companyCardDeleted]);

    const isReadOnly = (key: string): boolean => !!readOnlyProperties.find((value) => value === key);
    const onConfirmDelete = async () => {
        try {
            if (organization) {
                await deleteCompanyCard({
                    organizationId: organization.id,
                    id: organization.companyCard[CompanyCardField.ID],
                }).unwrap();

                enqueueSnackbar(t("generic_deleteSuccessfully"), {variant: "success"});
                deleteCompanyCardModal.toggleIsOpen();
            }
        } catch (e) {
            //
        }
    };
    const onSubmit = async (requestData: Partial<Organization>) => {
        const updateData = {
            id: requestData.id,
            name: requestData?.name ?? "",
            country: requestData?.country ?? "",
            locked: requestData?.locked ?? false,
            lockedReason: requestData?.lockedReason ?? "",
            productiveStage: requestData?.productiveStage ?? "",
            ...(requestData?.mainDKVCustomerNumber ? {mainDKVCustomerNumber: requestData?.mainDKVCustomerNumber} : {}),
        };
        await updateOrganization(updateData as OrganizationUpdateRequest).unwrap();
    };

    return (
        <>
            <LabeledSection
                title={t("generic_general")}
                buttons={[
                    {
                        key: "update",
                        tooltip: "action_update",
                        disabled: updateOrganizationModal.isOpen,
                        minRequiredPermissionToSee: PermissionLevel.MANAGER,
                        callback: () => updateOrganizationModal.setIsOpen(true),
                        icon: <EditIcon />,
                    },
                ]}
            >
                <LabeledValue label={t("organizations_name")} content={organization?.name} />
                <LabeledValue
                    label={t("organizations_mainDKVCustomerNumber")}
                    content={organization?.mainDKVCustomerNumber}
                />
                <LabeledValue
                    label={t("generic_country")}
                    content={getTranslation({
                        context: TranslationContext.COUNTRY,
                        value: organization?.country,
                        t,
                    })}
                />
                <LabeledValue
                    label={t("organizations_globalLocked")}
                    content={organization?.locked ? t("generic_yes") : t("generic_no")}
                />
                <LabeledValue label={t("organizations_lockedReason")} content={organization?.lockedReason} />
                <LabeledValue label={t("organizations_adminEmail")} content={organization?.adminEmail} />
                <LabeledValue
                    label={t("organizations_adminLanguage")}
                    content={getTranslation({
                        context: TranslationContext.LANGUAGE,
                        value: organization?.adminLanguage,
                        t,
                    })}
                />
                <LabeledValue label={t("organizations_productiveStage")} content={organization?.productiveStage} />
                <UpdateDialog
                    modal={updateOrganizationModal}
                    titleKey={"title_editOrganization"}
                    entity={organization}
                    validationSchema={getOrganizationUpdateValidationSchema}
                    onSubmit={onSubmit}
                    inputFields={[
                        {
                            translationKey: "organizations_name",
                            fieldName: OrganizationField.NAME,
                            required: true,
                            disabled: isReadOnly("name"),
                        },
                        {
                            translationKey: "organizations_mainDKVCustomerNumber",
                            fieldName: OrganizationField.MAIN_DKV_CUSTOMER_NUMBER,
                            required: true,
                            render: organization
                                ? ({inputField, form}) => {
                                      const {translationKey, fieldName, disabled} = inputField;
                                      return (
                                          <Grid item xs={6}>
                                              <FormControl fullWidth>
                                                  <AsyncIdListAutocomplete<DkvCustomerNumber>
                                                      label={t(translationKey)}
                                                      queryHook={useOrganizationDKVCustomerNumberListQuery}
                                                      queryId={organization.id}
                                                      queryField={DkvCustomerNumberField.DKV_CUSTOMER_NUMBER}
                                                      idField={DkvCustomerNumberField.DKV_CUSTOMER_NUMBER}
                                                      disabled={disabled}
                                                      value={form.values.mainDKVCustomerNumber}
                                                      onChange={(value) => {
                                                          if (value?.dkvCustomerNumber) {
                                                              form.setFieldValue(
                                                                  String(fieldName),
                                                                  value.dkvCustomerNumber
                                                              );
                                                          }
                                                      }}
                                                      key={"dkvCustomerNumber"}
                                                  />
                                              </FormControl>
                                          </Grid>
                                      );
                                  }
                                : undefined,
                        },
                        {
                            translationKey: "generic_country",
                            fieldName: OrganizationField.COUNTRY,
                            type: InputFieldType.COUNTRY,
                            required: true,
                            disabled: isReadOnly("country"),
                        },
                        {
                            translationKey: "organizations_globalLocked",
                            fieldName: OrganizationField.LOCKED,
                            type: InputFieldType.SELECT,
                            required: true,
                            gridProps: {xs: 6},
                            selectOptions: [
                                {value: true, label: t("generic_yes")},
                                {value: false, label: t("generic_no")},
                            ],
                        },
                        {
                            translationKey: "organizations_productiveStage",
                            fieldName: OrganizationField.PRODUCTIVE_STAGE,
                            required: true,
                            type: InputFieldType.ORGANIZATION_STAGE_SELECT,
                            fieldProps: {requestParams: {showTotal: true, organizationId: organization?.id}},
                            gridProps: {xs: 12},
                        },
                        {
                            translationKey: "organizations_lockedReason",
                            fieldName: OrganizationField.LOCKED_REASON,
                            minRows: 10,
                            multiline: true,
                            gridProps: {xs: 12},
                        },
                    ]}
                    initialValues={{
                        name: organization?.name ?? "",
                        country: organization?.country ?? "",
                        locked: organization?.locked ?? false,
                        lockedReason: organization?.lockedReason ?? "",
                        adminEmail: organization?.adminEmail ?? "",
                        adminLanguage: organization?.adminLanguage ?? "",
                        productiveStage: organization?.productiveStage ?? "",
                        mainDKVCustomerNumber: organization?.mainDKVCustomerNumber ?? "",
                    }}
                />
            </LabeledSection>
            <LabeledSection title={t("generic_companyCard")} buttons={companyCardActionButtons}>
                {companyCardDetail && organization?.companyCard ? (
                    <>
                        {renderCompanyCard(companyCardDetail)}
                        <ConfirmDialog
                            title={t("title_deleteCompanyCard")}
                            titleConfirm={t("generic_confirmDelete")}
                            isOpen={deleteCompanyCardModal.isOpen}
                            onConfirm={onConfirmDelete}
                            onClose={deleteCompanyCardModal.toggleIsOpen}
                        >
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <Typography variant={"body1"}>{t("title_bodyDeleteCompanyCard")}</Typography>
                                </Grid>
                                <LabeledValue
                                    label={t("organizations_name")}
                                    content={organization?.name}
                                    xs={6}
                                    md={6}
                                />
                                <LabeledValue
                                    label={t("organizations_mainDKVCustomerNumber")}
                                    content={organization?.mainDKVCustomerNumber}
                                    xs={6}
                                    md={6}
                                />
                                {renderCompanyCard(companyCardDetail)}
                            </Grid>
                        </ConfirmDialog>
                    </>
                ) : (
                    <>
                        <Typography variant={"body1"}>{EMPTY_UI}</Typography>
                    </>
                )}
            </LabeledSection>
            <AddEditCompanyCard
                modal={addEditCompanyCardModal}
                organization={organization}
                companyCard={organization?.companyCard ? companyCardDetail : undefined}
            />
        </>
    );
}
